import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ContactForm from './components/ContactForm';

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#0D0D0D] font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#0D0D0D]/95 backdrop-blur-md shadow-lg shadow-black/30' : 'bg-transparent'}`}>
        <div className="px-5 md:px-8 py-4 md:py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/5a93e3026f3c378ff1b81daef74f8459.png"
              alt="AgenteFinanciero Logo"
              className="h-8 md:h-10 w-auto object-contain brightness-0 invert"
            />
          </div>
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/familia" className="text-sm font-medium text-white/70 hover:text-[#E8A045] transition-colors whitespace-nowrap cursor-pointer tracking-wide">
              Para tu Familia
            </Link>
            <Link to="/agente-financiero" className="text-sm font-medium text-white/70 hover:text-[#E8A045] transition-colors whitespace-nowrap cursor-pointer tracking-wide">
              Carrera como Agente
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
          </button>
        </div>
        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-[#0D0D0D]/98 border-t border-white/10 px-5 py-4 flex flex-col gap-4">
            <Link to="/familia" onClick={() => setMenuOpen(false)} className="text-sm font-medium text-white/70 hover:text-[#E8A045] transition-colors whitespace-nowrap cursor-pointer tracking-wide py-2">
              Para tu Familia
            </Link>
            <Link to="/agente-financiero" onClick={() => setMenuOpen(false)} className="text-sm font-medium text-white/70 hover:text-[#E8A045] transition-colors whitespace-nowrap cursor-pointer tracking-wide py-2">
              Carrera como Agente
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8fd2362fca08ebe20b72b65e5d73090f.jpg"
            alt="Familia latina feliz"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D]/90 via-[#0D0D0D]/65 to-[#0D0D0D]/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/80 via-transparent to-[#0D0D0D]/20"></div>
        </div>

        <div className="relative z-10 w-full px-5 md:px-8 pt-24 pb-20">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#E8A045]/10 border border-[#E8A045]/30 rounded-full px-4 py-2 mb-6 md:mb-8">
                <span className="w-2 h-2 bg-[#E8A045] rounded-full animate-pulse"></span>
                <span className="text-[#E8A045] text-xs md:text-sm font-medium tracking-wider uppercase">Planificación Financiera</span>
              </div>

              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white leading-[0.9] mb-6 md:mb-8 tracking-tight">
                Protección<br />
                para tu<br />
                <span className="text-[#E8A045]">familia.</span>
              </h1>

              <p className="text-lg md:text-xl lg:text-2xl text-white/60 mb-3 md:mb-4 font-light leading-relaxed max-w-xl">
                Una carrera para ti.
              </p>

              <p className="text-sm md:text-base text-white/50 mb-10 md:mb-14 leading-relaxed max-w-lg">
                Elige si hoy quieres proteger a los tuyos o construir ingresos como AgenteFinanciero — part time o full time.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/familia" className="cursor-pointer whitespace-nowrap">
                  <div className="group bg-[#E8A045] hover:bg-[#d4913a] transition-all duration-300 rounded-full px-6 md:px-8 py-4 flex items-center gap-3 shadow-lg shadow-[#E8A045]/20 hover:shadow-[#E8A045]/40 hover:-translate-y-0.5 transform">
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-heart-fill text-[#0D0D0D] text-lg"></i>
                    </div>
                    <span className="font-bold text-[#0D0D0D] text-sm md:text-base">Quiero proteger a mi familia</span>
                  </div>
                </Link>
                <Link to="/agente-financiero" className="cursor-pointer whitespace-nowrap">
                  <div className="group bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 transition-all duration-300 rounded-full px-6 md:px-8 py-4 flex items-center gap-3 hover:-translate-y-0.5 transform">
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                      <i className="ri-briefcase-fill text-white text-lg"></i>
                    </div>
                    <span className="font-bold text-white text-sm md:text-base">Quiero ser AgenteFinanciero</span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/30 text-xs tracking-widest uppercase">Scroll</span>
          <i className="ri-arrow-down-line text-white/30 text-lg"></i>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 md:py-24 px-5 md:px-8 bg-[#0D0D0D] flex flex-col items-center">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-[#E8A045]/10 border border-[#E8A045]/30 rounded-full px-4 py-2 mb-5">
            <span className="w-2 h-2 bg-[#E8A045] rounded-full animate-pulse"></span>
            <span className="text-[#E8A045] text-xs font-medium tracking-wider uppercase">Mira este video</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Conoce más sobre <span className="text-[#E8A045]">nuestra misión</span>
          </h2>
        </div>
        <div
          className="relative w-full max-w-[380px] rounded-2xl overflow-hidden shadow-2xl shadow-black/60 border border-white/10"
          style={{ aspectRatio: '9/16' }}
        >
          <iframe
            src="https://www.youtube.com/embed/DmQHWG9-Vw0?rel=0&modestbranding=1&playsinline=1"
            title="AgenteFinanciero Video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            style={{ border: 'none' }}
          ></iframe>
        </div>
      </section>

      {/* Filter Message */}
      <section className="py-12 md:py-16 px-5 md:px-8 bg-[#111111]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block border-l-4 border-[#E8A045] pl-5 md:pl-6 text-left">
            <p className="text-white/80 text-base md:text-lg leading-relaxed font-light">
              <strong className="text-white font-semibold">Esto es solo para personas serias</strong> que ya están pensando en su futuro y el de su familia. Si solo tienes curiosidad, guarda esta página para más adelante.
            </p>
          </div>
        </div>
      </section>

      {/* Two Paths Section */}
      <section className="py-16 md:py-24 px-5 md:px-8 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight">
              Dos caminos. <span className="text-[#E8A045]">Un propósito.</span>
            </h2>
            <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto">Elige el que más resuena contigo hoy.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Card 1 - Familia */}
            <Link to="/familia" className="cursor-pointer group">
              <div className="relative overflow-hidden rounded-2xl bg-[#161616] border border-white/5 hover:border-[#E8A045]/30 transition-all duration-500 hover:-translate-y-2 shadow-xl hover:shadow-2xl hover:shadow-[#E8A045]/10">
                <div className="absolute inset-0 z-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=happy%20latin%20family%20parents%20with%20children%20at%20home%20warm%20cozy%20living%20room%20natural%20light%20smiling%20together%20safe%20protected%20modern%20home%20interior%20lifestyle%20photography&width=800&height=600&seq=familia-card-001&orientation=landscape"
                    alt="Familia protegida"
                    className="w-full h-full object-cover object-top opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161616] via-[#161616]/80 to-transparent"></div>
                </div>
                <div className="relative z-10 p-7 md:p-10">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#E8A045]/10 rounded-xl mb-6 md:mb-8 border border-[#E8A045]/20">
                    <i className="ri-shield-heart-fill text-[#E8A045] text-2xl"></i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-white mb-3 md:mb-4 leading-tight">
                    Quiero proteger<br />a mi familia
                  </h3>
                  <p className="text-white/50 text-sm md:text-base leading-relaxed mb-6 md:mb-8">
                    Seguro, retiro y protección explicados en lenguaje sencillo, sin compromiso.
                  </p>
                  <div className="space-y-3 mb-8 md:mb-10">
                    {[
                      'Que puedan seguir pagando casa, comida y escuela',
                      'Que no hereden deudas ni problemas',
                      'Retiro que no dependa solo del gobierno',
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-[#E8A045] text-sm"></i>
                        </div>
                        <span className="text-white/60 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[#E8A045] font-semibold text-sm group-hover:gap-4 transition-all duration-300">
                    <span>Explorar esta opción</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            </Link>

            {/* Card 2 - Carrera */}
            <Link to="/agente-financiero" className="cursor-pointer group">
              <div className="relative overflow-hidden rounded-2xl bg-[#161616] border border-white/5 hover:border-[#E8A045]/30 transition-all duration-500 hover:-translate-y-2 shadow-xl hover:shadow-2xl hover:shadow-[#E8A045]/10">
                <div className="absolute inset-0 z-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=confident%20latin%20professional%20financial%20advisor%20in%20modern%20office%20dark%20background%20suit%20smiling%20career%20success%20business%20growth%20ambition%20premium%20lifestyle%20photography&width=800&height=600&seq=carrera-card-001&orientation=landscape"
                    alt="Carrera como agente"
                    className="w-full h-full object-cover object-top opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161616] via-[#161616]/80 to-transparent"></div>
                </div>
                <div className="relative z-10 p-7 md:p-10">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#E8A045]/10 rounded-xl mb-6 md:mb-8 border border-[#E8A045]/20">
                    <i className="ri-briefcase-fill text-[#E8A045] text-2xl"></i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-black text-white mb-3 md:mb-4 leading-tight">
                    Quiero una carrera<br />como Agente Financiero
                  </h3>
                  <p className="text-white/50 text-sm md:text-base leading-relaxed mb-6 md:mb-8">
                    No desperdicies tus estudios y experiencia, aprovecha esta&nbsp; Oportunidad para generar ingresos ayudando a otras familias a organizar su futuro financiero.
                  </p>
                  <div className="space-y-3 mb-8 md:mb-10">
                    {[
                      'Opción part time o full time',
                      'Formación para entender productos de seguro y retiro',
                      'Ingresos construidos ayudando a familias',
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-[#E8A045] text-sm"></i>
                        </div>
                        <span className="text-white/60 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-[#E8A045] font-semibold text-sm group-hover:gap-4 transition-all duration-300">
                    <span>Explorar esta opción</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 px-5 md:px-8 bg-[#111111] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:divide-x divide-white/10">
            {[
              { value: '150,000+', label: 'Familias protegidas', icon: 'ri-shield-heart-line' },
              { value: '98%', label: 'Satisfacción de clientes', icon: 'ri-star-line' },
              { value: '15+', label: 'Años de experiencia', icon: 'ri-award-line' },
            ].map((stat, i) => (
              <div key={i} className={`text-center px-4 md:px-8 ${i > 0 ? 'border-t sm:border-t-0 border-white/10 pt-8 sm:pt-0' : ''}`}>
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-[#E8A045] text-2xl`}></i>
                </div>
                <div className="text-4xl md:text-5xl font-black text-white mb-2">{stat.value}</div>
                <div className="text-white/40 text-sm tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 md:py-24 px-5 md:px-8 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
            <div>
              <div className="w-12 h-12 flex items-center justify-center mb-6 md:mb-8">
                <i className="ri-double-quotes-l text-[#E8A045] text-5xl opacity-60"></i>
              </div>
              <blockquote className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-6 md:mb-8">
                "Pensé que los seguros eran solo para ricos. Ahora sé que son para personas que aman a su familia."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#E8A045]/20 flex items-center justify-center border border-[#E8A045]/30 flex-shrink-0">
                  <span className="text-[#E8A045] font-bold text-sm">JP</span>
                </div>
                <div>
                  <div className="text-white font-semibold">Jorge Peraza</div>
                  <div className="text-white/40 text-sm">Padre de familia, Buena Park, CA</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden h-64 md:h-80 w-full">
                <img
                  src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/9709ae77a9da437c79fc536de8c4a68e.png"
                  alt="Cliente satisfecho"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D]/40 to-transparent rounded-2xl"></div>
              </div>
              <div className="absolute -bottom-4 -left-2 md:-left-4 bg-[#E8A045] rounded-xl px-4 md:px-6 py-3 md:py-4 shadow-xl shadow-[#E8A045]/20">
                <div className="flex items-center gap-1 md:gap-2">
                  {[...Array(5)].map((_, s) => (
                    <i key={s} className="ri-star-fill text-[#0D0D0D] text-xs md:text-sm"></i>
                  ))}
                </div>
                <div className="text-[#0D0D0D] font-bold text-xs md:text-sm mt-1">5 estrellas</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 md:py-24 px-5 md:px-8 bg-[#111111] border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 bg-[#E8A045]/10 border border-[#E8A045]/30 rounded-full px-4 py-2 mb-5">
              <span className="w-2 h-2 bg-[#E8A045] rounded-full animate-pulse"></span>
              <span className="text-[#E8A045] text-xs font-medium tracking-wider uppercase">Contáctanos</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight">
              ¿Listo para dar el <span className="text-[#E8A045]">primer paso?</span>
            </h2>
            <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto">
              Cuéntanos qué te interesa y un agente se pondrá en contacto contigo pronto.
            </p>
          </div>

          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111111] border-t border-white/5 py-10 md:py-12 px-5 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-6 md:flex-row md:justify-between">
          <img
            src="https://static.readdy.ai/image/1a62d479ea6bd5856073579187b5ee28/5a93e3026f3c378ff1b81daef74f8459.png"
            alt="AgenteFinanciero Logo"
            className="h-8 md:h-10 w-auto object-contain brightness-0 invert opacity-60"
          />
          <p className="text-white/30 text-xs md:text-sm text-center">
            © 2025 AgenteFinanciero. Protegiendo familias y construyendo carreras.
          </p>
          <div className="flex items-center gap-5 md:gap-6 text-sm">
            <a href="#" className="text-white/30 hover:text-[#E8A045] transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm">Privacidad</a>
            <a href="#" className="text-white/30 hover:text-[#E8A045] transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm">Términos</a>
            <a href="#" className="text-white/30 hover:text-[#E8A045] transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
